import { eAtlas, eAtlasFrame, eTextures, Game, rnd2 } from "../../Game";
import { WrappedArray } from "../../Utils/Collections/WrapperArray";
import { AtlasUtils } from "../../Utils/Atlas/AtlasUtils";
import { TIMER } from "../Gameplay";
import { RoundManager } from "../Round/RoundManager";
import { GrandstandZoneMng } from "./GrandstandZone";



class SmokeBomb {
    private static _setups: any[];
    private static _colorGraphs: pc.CurveSet[];
    private static _glowMat: pc.StandardMaterial;

    private _partSysEnt: pc.Entity;
    private _glowEnt: pc.Entity;
    private _glowRotChangeTime: number;

    get enabled(): boolean { return this._partSysEnt.enabled; }
    set enabled(val: boolean) {
        if (this._partSysEnt.enabled != val) {
            this._partSysEnt.enabled = val;
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(pos: pc.Vec3, colorId?: number, setupId?: number) {
        if (!SmokeBomb._setups)
            this.createSetups();

        // Particle System
        if (setupId == undefined)
            setupId = rnd2.randomIntIn(0, SmokeBomb._setups.length - 1);

        if (colorId == undefined)
            colorId = rnd2.randomIntIn(0, SmokeBomb._colorGraphs.length - 1);

        let setupBase = SmokeBomb._setups[setupId];
        let setup: any = {
            startAngle: 1,
            startAngle2: 360,
            preWarm: true,
            loop: true,
            lighting: false,
            halfLambert: false,
            depthWrite: false,
            depthSoftening: false,
            sort: pc.PARTICLESORT_NEWER_FIRST,
            stretch: 0,
            alignToMotion: false,
            emitterShape: pc.EMITTERSHAPE_SPHERE,
            emitterRadius: 0,
            emitterRadiusInner: 0,
            wrap: false,
            localSpace: false,
            screenSpace: false,
            orientation: pc.PARTICLEORIENTATION_SCREEN,
            colorMap: Game.app.assets.find(eTextures.partSmoke, pc.eAssetType.texture).resource,
            colorGraph: SmokeBomb._colorGraphs[colorId],
            layers: [Game.app.scene.layers.getLayerByName("Immediate").id]
        };

        for (let key of Object.keys(setupBase)) {
            setup[key] = setupBase[key];
        }

        this._partSysEnt = new pc.Entity();
        this._partSysEnt.addComponent(pc.eCompomentType.particlesystem, setup);
        this._partSysEnt.setPosition(pos);
        Game.app.root.addChild(this._partSysEnt);

        // Zare
        this._glowEnt = new pc.Entity();

        this._glowEnt.addComponent(pc.eCompomentType.model, {
            type: pc.eModelType.plane,
            castShadows: false,
            receiveShadows: false,
            isStatic: true,
            material: SmokeBomb._glowMat,
        });

        this._glowEnt.model.layers = [Game.app.scene.layers.getLayerByName("UI").id];
        this._glowEnt.setEulerAngles(90, 0, 0);
        this._glowEnt.setLocalScale(1.5, 1.5, 1.5);
        this._partSysEnt.addChild(this._glowEnt);

        // Cas zmeny rotace zare        
        this._glowRotChangeTime = TIMER.time + rnd2.randomIntIn(200, 300);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): void {
        if (TIMER.time >= this._glowRotChangeTime) {
            this._glowRotChangeTime = TIMER.time + rnd2.randomIntIn(200, 300);
            this._glowEnt.setEulerAngles(90, 0, rnd2.randomIntIn(-45, 45));
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private createSetups(): void {
        SmokeBomb._setups = [];
        SmokeBomb._colorGraphs = [];

        // Modra
        let colorGraph = new pc.CurveSet([
            [0, 86 / 255, 1, 1],
            [0, 86 / 255, 1, 1],
            [0, 1]
        ]);
        colorGraph.type = pc.CURVE_SPLINE;
        SmokeBomb._colorGraphs.push(colorGraph);

        // Bila
        colorGraph = new pc.CurveSet([
            [0, 0.8],
            [0, 0.8],
            [0, 1]
        ]);
        colorGraph.type = pc.CURVE_SPLINE;
        SmokeBomb._colorGraphs.push(colorGraph);

        // Jednotliva nastaveni
        let setup = {
            numParticles: 20,
            lifetime: 8,
            rate: 0.4,
            rate2: 0.4,
            intensity: 0.75,
            blend: pc.BLEND_NONE,
            velocityGraph: new pc.CurveSet([
                [0, -0.1, 1, -0.5],
                [0, 0.25, 0.35, 1.5, 1, 0],
                [0, 0]
            ]),
            velocityGraph2: new pc.CurveSet([
                [0, -0.4, 1, -1],
                [0, 0.1, 0.35, 1, 1, 0],
                [0, 0]
            ]),
            scaleGraph: new pc.Curve([0, 0.175, 1, 3]),
            alphaGraph: new pc.Curve([0, 0.75, 1, 0]),
        }
        SmokeBomb._setups.push(setup);

        setup = {
            numParticles: 20,
            lifetime: 7,
            rate: 0.4,
            rate2: 0.4,
            intensity: 0.5,
            blend: pc.BLEND_NONE,
            velocityGraph: new pc.CurveSet([
                [0, 0.5, 1, -2.5],
                [0, 0.25, 0.4, 0.75, 1, 0],
                [0, 0]
            ]),
            velocityGraph2: new pc.CurveSet([
                [0, 0.75, 1, -1.5],
                [0, 0.5, 0.4, 1.25, 1, 0.25],
                [0, 0]
            ]),
            scaleGraph: new pc.Curve([0, 0.175, 1, 3.5]),
            alphaGraph: new pc.Curve([0, 0.75, 1, 0]),
        }
        SmokeBomb._setups.push(setup);

        setup = {
            numParticles: 20,
            lifetime: 8,
            rate: 0.4,
            rate2: 0.4,
            intensity: 0.5,
            blend: pc.BLEND_NONE,
            velocityGraph: new pc.CurveSet([
                [0, 0.1, 1, -0.5],
                [0, 0.25, 0.35, 1.5, 1, 0],
                [0, -0.3]
            ]),
            velocityGraph2: new pc.CurveSet([
                [0, 0.4, 1, 0.75],
                [0, 0.1, 0.35, 1, 1, 0],
                [0, -0.3]
            ]),
            scaleGraph: new pc.Curve([0, 0.175, 1, 3.5]),
            alphaGraph: new pc.Curve([0, 0.75, 1, 0]),
        }
        SmokeBomb._setups.push(setup);

        // Material zare
        let atlas = Game.getAtlas(eAtlas.def1);
        let rc = AtlasUtils.getAtlasFrameRegion(atlas, eAtlasFrame.smokeBombGlow);
        let mat = SmokeBomb._glowMat = new pc.StandardMaterial();

        mat.blendType = pc.BLEND_NORMAL;
        mat.opacity = 0.5;
        mat.diffuse.set(0, 0, 0);
        mat.emissive.set(1, 1, 0.9);
        mat.emissiveTint = true;
        mat.emissiveMap = atlas.texture;
        mat.emissiveMapOffset.set(rc.x, rc.y);
        mat.emissiveMapTiling.set(rc.z, rc.w * 0.95);
        mat.emissiveIntensity = 1;
        mat.opacityMap = atlas.texture;
        mat.opacityMapOffset.copy(mat.emissiveMapOffset);
        mat.opacityMapTiling.copy(mat.emissiveMapTiling);
        mat.useLighting = false;
        mat.update();
    }
}



export interface SmokeBombSetup {
    zoneId: number;
    zoneHPosRatio: number;
    zoneVPosRatio: number;
    colorId?: number;
    setupId?: number;
    minAngle?: number;
    maxAngle?: number;
}



export class SmokeBombMng {
    private static readonly SETUPS: SmokeBombSetup[][] = [
        [
            { zoneId: 0, zoneHPosRatio: 0.9, zoneVPosRatio: 0.1, colorId: 1, setupId: 2, maxAngle: 79 },
            { zoneId: 1, zoneHPosRatio: 0.15, zoneVPosRatio: 0.3, colorId: 0, setupId: 0, maxAngle: 95 },
            { zoneId: 1, zoneHPosRatio: 0.4, zoneVPosRatio: 0.4, colorId: 1, setupId: 2 },
            { zoneId: 1, zoneHPosRatio: 0.6, zoneVPosRatio: 0.05, colorId: 0, setupId: 1 },
            { zoneId: 1, zoneHPosRatio: 0.85, zoneVPosRatio: 0.22, colorId: 1, setupId: 0, minAngle: 80 },
            { zoneId: 2, zoneHPosRatio: 0.1, zoneVPosRatio: 0.4, colorId: 1, setupId: 1, minAngle: 100 }
        ],
        [
            { zoneId: 0, zoneHPosRatio: 0.9, zoneVPosRatio: 0.3, colorId: 0, setupId: 0, maxAngle: 79 },
            { zoneId: 1, zoneHPosRatio: 0.1, zoneVPosRatio: 0.1, colorId: 1, setupId: 2, maxAngle: 95 },
            { zoneId: 1, zoneHPosRatio: 0.3, zoneVPosRatio: 0.3, colorId: 1, setupId: 1 },
            { zoneId: 1, zoneHPosRatio: 0.7, zoneVPosRatio: 0.4, colorId: 0, setupId: 0 },
            { zoneId: 1, zoneHPosRatio: 0.85, zoneVPosRatio: 0.15, colorId: 0, setupId: 2, minAngle: 80 },
            { zoneId: 2, zoneHPosRatio: 0.1, zoneVPosRatio: 0.15, colorId: 1, setupId: 1, minAngle: 100 }
        ]
    ]

    /**
     * Index atkualniho nastaveni
     */
    private _setupId: number;
    /**
     * Pole se vsemi dymovnicemi
     */
    private _smokeBombs: SmokeBomb[];
    /**
     * Pole aktivnich dymovnic
     */
    private _actSmokeBombs: WrappedArray<SmokeBomb>;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(zones: GrandstandZoneMng) {
        // Vytvorit dymovnice
        let setup = SmokeBombMng.SETUPS[(this._setupId = rnd2.randomIntIn(0, SmokeBombMng.SETUPS.length - 1))];

        this._smokeBombs = [];

        setup.forEach((data: SmokeBombSetup): void => {
            this._smokeBombs.push(new SmokeBomb(new pc.Vec3().copy(zones.getZonePos(data.zoneHPosRatio, data.zoneVPosRatio, data.zoneId)), data.colorId, data.setupId));
        });

        this._actSmokeBombs = new WrappedArray<SmokeBomb>();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): void {
        this._actSmokeBombs.forEach((bomb: SmokeBomb): void => { bomb.update(); });
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    prepareNewRound(): void {
        let roundMng = RoundManager.instance;

        if (roundMng.roundAttemptId == 0) {
            this._actSmokeBombs.clear();

            // Povolit/zakazat dymovnice podle uhlu kamery
            let goalCenterAngle = pc.math.RAD_TO_DEG * roundMng.roundSetup.goalCenterAngle;

            SmokeBombMng.SETUPS[this._setupId].forEach((data: SmokeBombSetup, bombId: number): void => {
                let bomb = this._smokeBombs[bombId];
                let minAngle = data.minAngle || 0;
                let maxAngle = data.maxAngle || 180;

                if (goalCenterAngle >= minAngle && goalCenterAngle <= maxAngle) {
                    bomb.enabled = true;
                    this._actSmokeBombs.add(bomb);
                }
                else {
                    bomb.enabled = false;
                }
            });
        }
    }
}
